import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Product, ProductVariation } from '../types/woocommerce';

interface CartItem {
  product: Product;
  variation?: ProductVariation;
  quantity: number;
  variationId?: number;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  
  // Actions
  addToCart: (product: Product, quantity?: number, variation?: ProductVariation) => void;
  removeFromCart: (productId: number, variationId?: number) => void;
  updateQuantity: (productId: number, quantity: number, variationId?: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  setCartOpen: (isOpen: boolean) => void;
  
  // Getters
  getItemCount: () => number;
  getSubtotal: () => number;
  getCartItem: (productId: number, variationId?: number) => CartItem | undefined;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addToCart: (product, quantity = 1, variation) => {
        set((state) => {
          const existingItemIndex = state.items.findIndex(
            (item) =>
              item.product.id === product.id &&
              item.variationId === variation?.id
          );

          if (existingItemIndex > -1) {
            // Si le produit existe déjà, augmenter la quantité
            const newItems = [...state.items];
            newItems[existingItemIndex].quantity += quantity;
            return { items: newItems };
          } else {
            // Sinon, ajouter un nouveau produit
            return {
              items: [
                ...state.items,
                {
                  product,
                  variation,
                  quantity,
                  variationId: variation?.id,
                },
              ],
            };
          }
        });
      },

      removeFromCart: (productId, variationId) => {
        set((state) => ({
          items: state.items.filter(
            (item) =>
              !(item.product.id === productId && item.variationId === variationId)
          ),
        }));
      },

      updateQuantity: (productId, quantity, variationId) => {
        if (quantity <= 0) {
          get().removeFromCart(productId, variationId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId && item.variationId === variationId
              ? { ...item, quantity }
              : item
          ),
        }));
      },

      clearCart: () => set({ items: [] }),

      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

      setCartOpen: (isOpen) => set({ isOpen }),

      getItemCount: () => {
        const state = get();
        return state.items.reduce((total, item) => total + item.quantity, 0);
      },

      getSubtotal: () => {
        const state = get();
        return state.items.reduce((total, item) => {
          const price = item.variation
            ? parseFloat(item.variation.price)
            : parseFloat(item.product.price);
          return total + price * item.quantity;
        }, 0);
      },

      getCartItem: (productId, variationId) => {
        const state = get();
        return state.items.find(
          (item) =>
            item.product.id === productId && item.variationId === variationId
        );
      },
    }),
    {
      name: 'woocommerce-cart',
      // Exclure isOpen de la persistance
      partialize: (state) => ({ items: state.items }),
    }
  )
);

